/* FASE 13 (WS16 — arquitectura de campañas) — Landing de campaña por audiencia.
   Plantilla que REUTILIZA los bloques corporativos reales (cero claims nuevos):
   /lp/organisers · /lp/breeders · /lp/events · /lp/single-horse · /lp/buyers
   - noindex (son destinos de campaña, no SEO orgánico).
   - Preserva UTM: los CTAs arrastran la query string completa.
   - Los pixels/analytics de campaña requieren claves del cliente (documentado). */
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";

import { H_SECTION, SUBLINE } from "@/components/marketing/Sections";
import { BRAND } from "@/config/brand";
import NotFound from "@/pages/NotFound";
import { usePageMeta } from "@/lib/seo";
import { usePlatformPricing } from "@/hooks/usePlatformPricing";

/* Cada audiencia mapea a copy corporativo REAL ya traducido x5. */
const AUDIENCES = {
  organisers: {
    titleKey: "fo.title", subKey: "fo.sub",
    blocks: [["fo.b1t", "fo.b1d"], ["fo.b4t", "fo.b4d"], ["fo.b5t", "fo.b5d"]],
    ctaTo: "/create", ctaKey: "mk.cta",
  },
  breeders: {
    titleKey: "fo.b2t", subKey: "fo.b2d",
    blocks: [["fo.b1t", "fo.b1d"], ["fo.b3t", "fo.b3d"], ["fo.b6t", "fo.b6d"]],
    ctaTo: "/create", ctaKey: "mk.cta",
  },
  events: {
    titleKey: "fo.b3t", subKey: "fo.b3d",
    blocks: [["ft.f3t", "ft.f3d"], ["ft.f2t", "ft.f2d"], ["fo.b5t", "fo.b5d"]],
    ctaTo: "/create", ctaKey: "mk.cta",
  },
  "single-horse": {
    titleKey: "fo.b2t", subKey: "fo.pricingNote",
    blocks: [["fo.b1t", "fo.b1d"], ["ft.f4t", "ft.f4d"], ["fo.b5t", "fo.b5d"]],
    ctaTo: "/create", ctaKey: "mk.cta",
  },
  buyers: {
    titleKey: "fb.title", subKey: "fb.sub",
    blocks: [["fb.b3t", "fb.b3d"], ["fb.b4t", "fb.b4d"], ["fb.b5t", "fb.b5d"]],
    ctaTo: "/auctions", ctaKey: "nav.browseAuctions",
  },
};

export default function CampaignLanding() {
  const { audience } = useParams();
  // Platform Economics: los importes de la nota de pricing vienen de Admin → Economics
  const { fee, commission } = usePlatformPricing();
  const pricingParams = { fee: fee ?? "…", commission: commission ?? "…" };
  const { t } = useTranslation();
  const { search } = useLocation(); // preservación de UTM en todos los CTAs
  const cfg = AUDIENCES[audience];

  usePageMeta({
    title: cfg ? `${t(cfg.titleKey)} — ${BRAND.name}` : undefined,
    description: cfg ? t(cfg.subKey, pricingParams) : undefined,
    robots: "noindex, nofollow",
  });

  if (!cfg) return <NotFound />;

  return (
    <div data-testid={`lp-${audience}`}>
      <section className="max-w-[980px] mx-auto px-6 pt-20 pb-14 text-center flex flex-col items-center gap-5">
        <h1 className={`${H_SECTION} text-[#1d1d1f]`} data-testid="lp-title">{t(cfg.titleKey)}</h1>
        <p className={`${SUBLINE} text-[#4b4b50] max-w-2xl`}>{t(cfg.subKey, pricingParams)}</p>
        <Link
          to={`${cfg.ctaTo}${search}`}
          data-testid="lp-cta"
          className="mt-4 inline-flex items-center rounded-full bg-[#0A0A0A] text-white text-sm font-semibold px-6 py-3 hover:bg-[#1d1d1f] transition-colors"
        >
          {t(cfg.ctaKey)}
        </Link>
      </section>
      <section className="bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-3 gap-x-12 gap-y-10">
          {cfg.blocks.map(([tk, dk], i) => (
            <div key={tk} className="flex flex-col gap-2" data-testid={`lp-block-${i + 1}`}>
              <h2 className="text-[17px] font-semibold text-[#1d1d1f]">{t(tk)}</h2>
              <p className="text-sm text-[#3f3f44] leading-relaxed">{t(dk)}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="max-w-[780px] mx-auto px-6 py-16 text-center">
        <Link
          to={`${cfg.ctaTo}${search}`}
          data-testid="lp-cta-final"
          className="inline-flex items-center rounded-full bg-[#0A0A0A] text-white text-sm font-semibold px-6 py-3 hover:bg-[#1d1d1f] transition-colors"
        >
          {t(cfg.ctaKey)}
        </Link>
      </section>
    </div>
  );
}
